import {
  CalendarOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { RefProps } from '@lib/types'
import { Button, Calendar, Collapse, ConfigProvider, Input, Modal } from 'antd'
import zhTW from 'antd/lib/locale/zh_TW'
import cx from 'classnames'
import React from 'react'
import styled from 'styled-components'
import Select from '../Select'

type Moment = import('moment').Moment
type RangeValue = [Moment | null, Moment | null]

type Props = RefProps & {
  value?: RangeValue
  onChange?: (value: RangeValue, dateStrings: [string, string]) => void
  allowClear?: boolean
  placeholder?: string
  format?: string
  modalTitle?: React.ReactNode
  okText?: React.ReactNode
  cancelText?: React.ReactNode
  disabledDate?: (currentDate: Moment) => boolean
}

const EMPTY_RANGE: RangeValue = [null, null]

const normalizeRange = (value?: RangeValue): RangeValue => value || EMPTY_RANGE

const isSameDay = (a: Moment | null, b: Moment | null) => {
  if (!a && !b) {
    return true
  }

  if (!a || !b) {
    return false
  }

  return a.isSame(b, 'day')
}

const isSameRange = (a: RangeValue, b: RangeValue) =>
  isSameDay(a[0], b[0]) && isSameDay(a[1], b[1])

const YEAR_RANGE = 5

const CalendarHeader = ({
  value,
  onChange,
}: {
  value: Moment
  type: 'month' | 'year'
  onChange: (date: Moment) => void
  onTypeChange: (type: 'month' | 'year') => void
}) => {
  const currentYear = value.year()
  const currentMonth = value.month()

  const yearOptions = React.useMemo(() => {
    const base = new Date().getFullYear()
    return Array.from({ length: YEAR_RANGE * 2 + 1 }, (_, i) => {
      const y = base - YEAR_RANGE + i
      return (
        <Select.Option key={y} value={y}>
          {`${y} 年`}
        </Select.Option>
      )
    })
  }, [])

  const monthOptions = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => (
        <Select.Option key={i} value={i}>
          {`${i + 1} 月`}
        </Select.Option>
      )),
    []
  )

  const onPrevMonth = () => onChange(value.clone().subtract(1, 'month'))
  const onNextMonth = () => onChange(value.clone().add(1, 'month'))

  return (
    <CalendarHeaderRow>
      <Button type="text" size="small" onClick={onPrevMonth}>
        <LeftOutlined />
      </Button>

      <HeaderSelects>
        <Select
          size="small"
          value={currentYear}
          onChange={(y: number) => onChange(value.clone().year(y))}
          dropdownMatchSelectWidth={false}>
          {yearOptions}
        </Select>
        <Select
          size="small"
          value={currentMonth}
          onChange={(m: number) => onChange(value.clone().month(m))}
          dropdownMatchSelectWidth={false}>
          {monthOptions}
        </Select>
      </HeaderSelects>

      <Button type="text" size="small" onClick={onNextMonth}>
        <RightOutlined />
      </Button>
    </CalendarHeaderRow>
  )
}

const MobileRangePicker = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className = '',
      value,
      onChange,
      allowClear = true,
      placeholder,
      format = 'YYYY-MM-DD',
      modalTitle = '選擇日期區間',
      okText = '確定',
      cancelText = '取消',
      disabled = false,
      disabledDate,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)
    const [innerValue, setInnerValue] = React.useState<RangeValue>(
      normalizeRange(value)
    )
    const [draftRange, setDraftRange] = React.useState<RangeValue>(
      normalizeRange(value)
    )

    React.useEffect(() => {
      const normalized = normalizeRange(value)
      setInnerValue(prev => (isSameRange(normalized, prev) ? prev : normalized))
      if (!open) {
        setDraftRange(normalized)
      }
    }, [value, open])

    const dateStrings = React.useMemo<[string, string]>(
      () => [
        innerValue[0] ? innerValue[0].format(format) : '',
        innerValue[1] ? innerValue[1].format(format) : '',
      ],
      [format, innerValue]
    )

    const displayText = React.useMemo(() => {
      const start = dateStrings[0] || '開始日期'
      const end = dateStrings[1] || '結束日期'
      return `${start} -> ${end}`
    }, [dateStrings])

    const emitChange = (nextValue: RangeValue) => {
      const nextDateStrings: [string, string] = [
        nextValue[0] ? nextValue[0].format(format) : '',
        nextValue[1] ? nextValue[1].format(format) : '',
      ]
      onChange && onChange(nextValue, nextDateStrings)
    }

    const openModal = () => {
      if (disabled) {
        return
      }
      setDraftRange(innerValue)
      setOpen(true)
    }

    const onClear = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      setInnerValue(EMPTY_RANGE)
      setDraftRange(EMPTY_RANGE)
      emitChange(EMPTY_RANGE)
    }

    const onSelectStartDate = (date: Moment) => {
      setDraftRange(prev => {
        const nextStart = date || null
        let nextEnd = prev[1]

        if (nextStart && nextEnd && nextStart.isAfter(nextEnd, 'day')) {
          nextEnd = nextStart
        }

        return [nextStart, nextEnd]
      })
    }

    const onSelectEndDate = (date: Moment) => {
      setDraftRange(prev => {
        let nextStart = prev[0]
        const nextEnd = date || null

        if (nextStart && nextEnd && nextEnd.isBefore(nextStart, 'day')) {
          nextStart = nextEnd
        }

        return [nextStart, nextEnd]
      })
    }

    const onCancel = () => {
      setDraftRange(innerValue)
      setOpen(false)
    }

    const onConfirm = () => {
      setInnerValue(draftRange)
      emitChange(draftRange)
      setOpen(false)
    }

    const endDisabledDate = (currentDate: Moment) => {
      if (disabledDate && disabledDate(currentDate)) {
        return true
      }

      const startDate = draftRange[0]
      return !!(startDate && currentDate.isBefore(startDate, 'day'))
    }

    return (
      <ConfigProvider locale={zhTW}>
        <Container className={cx('c-mobile-range-picker', className)}>
          <StyledInput
            {...props}
            ref={ref}
            readOnly
            value={displayText}
            placeholder={placeholder}
            disabled={disabled}
            onClick={openModal}
            suffix={<CalendarOutlined />}
          />

          <StyledModal
            destroyOnClose
            title={modalTitle}
            visible={open}
            onCancel={onCancel}
            footer={null}
            centered>
            <AccordionWrapper
              accordion
              ghost
              defaultActiveKey={'start'}
              bordered={false}
              expandIcon={({ isActive }) => (
                <DownOutlined rotate={isActive ? 180 : 0} />
              )}>
              <Collapse.Panel
                header={`開始日${
                  draftRange[0] ? `: ${draftRange[0].format(format)}` : ''
                }`}
                key="start">
                <Calendar
                  fullscreen={false}
                  value={draftRange[0] || undefined}
                  disabledDate={disabledDate}
                  onSelect={onSelectStartDate}
                  headerRender={CalendarHeader}
                />
              </Collapse.Panel>

              <Collapse.Panel
                header={`結束日${
                  draftRange[1] ? `: ${draftRange[1].format(format)}` : ''
                }`}
                key="end">
                <Calendar
                  fullscreen={false}
                  value={draftRange[1] || undefined}
                  disabledDate={endDisabledDate}
                  onSelect={onSelectEndDate}
                  headerRender={CalendarHeader}
                />
              </Collapse.Panel>
            </AccordionWrapper>

            <ActionRow>
              {allowClear && (
                <Button ghost type="primary" onClick={onClear}>
                  清除
                </Button>
              )}
              <div>
                <Button onClick={onCancel}>{cancelText}</Button>
                <Button type="primary" onClick={onConfirm}>
                  {okText}
                </Button>
              </div>
            </ActionRow>
          </StyledModal>
        </Container>
      </ConfigProvider>
    )
  }
)

export default MobileRangePicker
const Container = styled.div`
  width: 100%;
`

const StyledInput = styled(Input)`
  cursor: pointer;

  &.ant-input-affix-wrapper {
    border-radius: 4px;
    background-color: ${p => p.theme.datePicker.bg};
    border-color: ${p => p.theme.datePicker.borderColor};
    color: ${p => p.theme.datePicker.color};

    &:hover,
    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${p => p.theme.datePicker.hoverAndFocusBorderColor};
      box-shadow: none;
    }

    &.ant-input-affix-wrapper-disabled {
      background: ${p => p.theme.datePicker.disabledBg};
      color: ${p => p.theme.datePicker.disabledColor};
    }

    input {
      background: transparent;
      color: ${p => p.theme.datePicker.color};
      cursor: pointer;

      &::placeholder {
        color: ${p => p.theme.datePicker.placeholderColor};
      }

      &[disabled] {
        color: ${p => p.theme.datePicker.disabledColor};
      }
    }
  }
`
const StyledModal = styled(Modal)`
  .ant-modal-content {
    background-color: ${p => p.theme.modal.bg};
    color: ${p => p.theme.modal.color};
  }

  .ant-modal-header {
    background-color: ${p => p.theme.modal.bg};
    border-bottom: ${p => p.theme.modal.footerBorderTop};

    .ant-modal-title {
      color: ${p => p.theme.modal.color};
    }
  }

  .ant-modal-close-x {
    color: ${p => p.theme.modal.color};
  }

  .ant-modal-body {
    padding-top: 12px;
  }

  /* Calendar mini 深色 */
  .ant-picker-calendar-mini {
    width: 100%;
    background: ${p => p.theme.datePicker.panel.bg};

    .ant-picker-panel {
      background: ${p => p.theme.datePicker.panel.bg};
      border-top: 1px solid ${p => p.theme.datePicker.panel.borderColor};
    }

    .ant-picker-header {
      color: ${p => p.theme.datePicker.panel.color};

      button {
        color: ${p => p.theme.datePicker.panel.prevAndNextBtnColor};

        &:hover {
          color: ${p => p.theme.datePicker.panel.prevAndNextBtnHoverColor};
        }
      }
    }

    .ant-picker-content th {
      color: ${p => p.theme.datePicker.panel.color};
    }

    .ant-picker-cell {
      color: ${p => p.theme.datePicker.panel.unselectColor};

      &.ant-picker-cell-in-view {
        color: ${p => p.theme.datePicker.panel.color};

        &.ant-picker-cell-today .ant-picker-cell-inner::before {
          border-color: ${p => p.theme.datePicker.panel.todayColor};
        }

        &.ant-picker-cell-selected .ant-picker-cell-inner {
          background: ${p => p.theme.datePicker.panel.selectedBg};
          color: ${p => p.theme.datePicker.panel.selectedColor};
        }
        &.ant-picker-cell-disabled {
          color: ${p => p.theme.datePicker.disabledColor};
        }
      }

      &:hover:not(.ant-picker-cell-selected) .ant-picker-cell-inner {
        background: ${p => p.theme.datePicker.panel.hoverBg};
        color: ${p => p.theme.datePicker.panel.hoverColor};
      }
    }

    .ant-select {
      color: ${p => p.theme.datePicker.panel.color};
    }

    .ant-radio-group {
      display: none;
    }
  }
`

const AccordionWrapper = styled(Collapse)`
  background: ${p => p.theme.modal.bg};

  .ant-collapse-item {
    border-bottom: 1px solid ${p => p.theme.datePicker.panel.borderColor};
  }

  .ant-collapse-header {
    font-weight: 500;
    color: ${p => p.theme.modal.color} !important;
  }

  .ant-collapse-content {
    background-color: ${p => p.theme.modal.bg};
    border-top: 1px solid ${p => p.theme.datePicker.panel.borderColor};
    color: ${p => p.theme.modal.color};
  }

  .ant-collapse-expand-icon {
    color: ${p => p.theme.modal.color};
  }
`

const ActionRow = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  background: ${p => p.theme.modal.bg};
`

const CalendarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;

  .ant-btn-text {
    color: ${p => p.theme.datePicker.panel.prevAndNextBtnColor};

    &:hover {
      color: ${p => p.theme.datePicker.panel.prevAndNextBtnHoverColor};
    }
  }
`

const HeaderSelects = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
